import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, RotateCcw } from 'lucide-react';
import { getOrder, amendAndResubmit } from '../../api/orders';
import { useAuth } from '../../auth/useAuth';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { StatusBadge, Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { ApproveOrderModal } from './ApproveOrderModal';
import { RejectOrderModal } from './RejectOrderModal';

function money(value: string | number) {
  return `£${Number(value).toFixed(2)}`;
}

function AmendSection({ orderId, lines, reference }: { orderId: string; lines: { id: string; customerCatalogItemId: string; quantity: number; customerCatalogItem?: { name: string } }[]; reference: string }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [ref, setRef] = useState(reference);
  const [quantities, setQuantities] = useState<Record<string, number>>(
    Object.fromEntries(lines.map((l) => [l.customerCatalogItemId, l.quantity])),
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      amendAndResubmit(orderId, {
        reference: ref,
        lines: Object.entries(quantities)
          .filter(([, qty]) => qty > 0)
          .map(([customerCatalogItemId, quantity]) => ({ customerCatalogItemId, quantity })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      showToast('Order amended and resubmitted.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not resubmit the order.')),
  });

  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium text-slate-600">
          Rejected - adjust quantities (0 to remove an item) and resubmit for approval.
        </span>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Input label="Who is this for?" value={ref} onChange={(e) => setRef(e.target.value)} />
        <div className="flex flex-col gap-2">
          {lines.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-700">{l.customerCatalogItem?.name ?? 'Item'}</span>
              <Input
                type="number"
                min="0"
                className="w-20 text-center"
                value={quantities[l.customerCatalogItemId] ?? 0}
                onChange={(e) =>
                  setQuantities((prev) => ({
                    ...prev,
                    [l.customerCatalogItemId]: Math.max(0, Math.floor(Number(e.target.value)) || 0),
                  }))
                }
              />
            </div>
          ))}
        </div>
        <Button
          className="self-start"
          onClick={() => {
            setError(null);
            mutation.mutate();
          }}
          isLoading={mutation.isPending}
        >
          Resubmit order
        </Button>
      </CardBody>
    </Card>
  );
}

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const orderQuery = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId!),
    enabled: !!orderId,
  });

  if (!orderId) return null;

  if (orderQuery.isLoading || !orderQuery.data) {
    return (
      <div className="py-14 text-center">
        <Spinner />
      </div>
    );
  }

  const order = orderQuery.data;
  const isRequester = order.requestingUserId === user?.id;
  const isSigningAuthorityHere =
    !!user &&
    (user.isMspAdmin || user.siteRoles.some((r) => r.siteId === order.siteId && r.role === 'SIGNING_AUTHORITY'));
  const total = order.lines.reduce((sum, l) => sum + Number(l.priceSnapshot) * l.quantity, 0);

  return (
    <div>
      <PageHeader
        breadcrumb={
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Link to="/orders" className="hover:text-slate-600">
              Orders
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span>{order.reference}</span>
          </div>
        }
        title={order.reference}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={order.status} kind="order" />
            {order.chargeType && <Badge tone="blue">{order.chargeType.replace('_', '-')}</Badge>}
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                navigate('/shop', {
                  state: {
                    reorder: {
                      siteId: order.siteId,
                      lines: order.lines.map((l) => ({
                        customerCatalogItemId: l.customerCatalogItemId,
                        quantity: l.quantity,
                        reference: l.reference ?? undefined,
                      })),
                    },
                  },
                })
              }
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reorder
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-6">
        {order.status === 'PENDING_APPROVAL' && isSigningAuthorityHere && (
          <Card>
            <CardBody className="flex items-center justify-between">
              <span className="text-sm text-slate-600">This order is awaiting your decision.</span>
              <div className="flex gap-2">
                <Button variant="danger" size="sm" onClick={() => setRejectOpen(true)}>
                  Reject
                </Button>
                <Button size="sm" onClick={() => setApproveOpen(true)}>
                  Approve
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader>
            <span className="text-sm font-medium text-slate-600">Items</span>
          </CardHeader>
          <Table>
            <Thead>
              <tr>
                <Th>Item</Th>
                <Th>Reference</Th>
                <Th>Quantity</Th>
                <Th>Unit price</Th>
                <Th>Line total</Th>
              </tr>
            </Thead>
            <tbody>
              {order.lines.map((line) => (
                <Tr key={line.id}>
                  <Td className="font-medium text-slate-900">{line.customerCatalogItem?.name ?? 'Item'}</Td>
                  <Td>{line.reference ?? <span className="text-slate-400">-</span>}</Td>
                  <Td>{line.quantity}</Td>
                  <Td>{money(line.priceSnapshot)}</Td>
                  <Td>{money(Number(line.priceSnapshot) * line.quantity)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <div className="flex justify-end border-t border-slate-100 px-5 py-3 text-sm font-semibold text-slate-900">
            Total: {money(total)}
          </div>
        </Card>

        {order.approvalActions && order.approvalActions.length > 0 && (
          <Card>
            <CardHeader>
              <span className="text-sm font-medium text-slate-600">Approval history</span>
            </CardHeader>
            <CardBody className="flex flex-col gap-2">
              {order.approvalActions.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Badge tone={a.decision === 'APPROVED' ? 'green' : 'red'}>{a.decision}</Badge>
                    {a.poNumber && <span className="text-slate-500">PO {a.poNumber}</span>}
                    {a.rejectionReason && <span className="text-slate-500">{a.rejectionReason}</span>}
                  </span>
                  <span className="text-slate-400">{new Date(a.actedAt).toLocaleString()}</span>
                </div>
              ))}
            </CardBody>
          </Card>
        )}

        {order.status === 'REJECTED' && isRequester && (
          <AmendSection orderId={order.id} lines={order.lines} reference={order.reference} />
        )}
      </div>

      <ApproveOrderModal order={order} open={approveOpen} onClose={() => setApproveOpen(false)} />
      <RejectOrderModal order={order} open={rejectOpen} onClose={() => setRejectOpen(false)} />
    </div>
  );
}
